import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Cpu, 
  Award, 
  MapPin, 
  Send, 
  Check, 
  Image as ImageIcon,
  CheckCircle,
  Link as LinkIcon
} from 'lucide-react';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';

interface ResearchResidencyProps {
  lang: 'en' | 'ar';
}

const ResearchImagePlaceholder: React.FC<{
  labelEn: string;
  labelAr: string;
  filename: string;
  dimensions: string;
  isAr: boolean;
}> = ({ labelEn, labelAr, filename, dimensions, isAr }) => {
  const [copied, setCopied] = useState(false);
  const [imageError, setImageError] = useState(false); // default to false to allow loading
  const path = `/images/research/${filename}`;

  const copyPath = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(path);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-full aspect-video rounded-xl bg-[#12161A] border border-[#262C31] hover:border-[#4E9BB8]/40 transition-all duration-300 relative group overflow-hidden shadow-sm">
      {!imageError ? (
        <div className="w-full h-full bg-[#05070A] flex items-center justify-center relative select-none">
          <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none z-10" />
          <img
            src={path}
            alt={labelEn}
            onError={() => setImageError(true)}
            loading="lazy"
            className="w-full h-full object-cover pointer-events-none transition-transform duration-700 group-hover:scale-[1.02]"
            draggable={false}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#05070A]/60 via-transparent to-[#05070A]/30 pointer-events-none z-10" />
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 relative">
          <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.02)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white/[0.03] pointer-events-none" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/[0.03] pointer-events-none" />

          <div className="z-20 text-center space-y-4 max-w-sm">
            <div className="w-12 h-12 rounded-lg bg-[#171C21] border border-[#262C31] flex items-center justify-center mx-auto text-[#9AA5AC] group-hover:text-[#4E9BB8] transition-all">
              <ImageIcon size={22} />
            </div>
            <div className="space-y-1.5">
              <h5 className="text-sm font-medium text-[#EDF0F1] transition-colors">
                {isAr ? labelAr : labelEn}
              </h5>
              <span className="text-[10px] font-mono text-[#9AA5AC] block">
                Target Dimensions: <span className="text-[#4E9BB8]">{dimensions}</span>
              </span>
            </div>

            <button 
              onClick={copyPath}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#171C21] border border-[#262C31] text-[#9AA5AC] hover:text-[#EDF0F1] text-[10px] font-mono transition-colors relative z-30"
            >
              {copied ? (
                <>
                  <CheckCircle size={10} className="text-[#639922]" />
                  <span>COPIED PATH</span>
                </>
              ) : (
                <>
                  <LinkIcon size={10} className="text-[#0E93A3]" />
                  <span>{filename}</span>
                </>
              )}
            </button>
          </div>

          <div className="absolute bottom-4 right-4 left-4 flex justify-between items-center text-[9px] font-mono text-[#66707A] opacity-0 group-hover:opacity-100 transition-opacity">
            <span>VEXL RESEARCH LABS</span>
            <span>IMAGE_PLACEHOLDER</span>
          </div>
        </div>
      )}
    </div>
  );
};

export const ResearchResidency: React.FC<ResearchResidencyProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    domain: 'spatial-ml',
    proposal: ''
  });

  const t = {
    title: isAr ? 'برنامج الإقامة البحثية' : 'Research Residency Program',
    subtitle: isAr
      ? 'انضم إلينا كزميل زائر لمدة 3-6 أشهر للعمل على حل مشكلات حقيقية في الذكاء الاصطناعي الجغرافي والبيئي.'
      : 'Join VEXL labs as a visiting fellow for 3-6 months. Direct access to our compute clusters and datasets.',
    benefitsTitle: isAr ? 'مزايا برنامج الإقامة' : 'Fellowship Benefits',
    benefits: [
      {
        icon: Cpu,
        title: isAr ? 'قدرة حوسبية هائلة' : 'Supercompute Access',
        desc: isAr ? 'وصول كامل لمصفوفات معالجة البيانات وبطاقات الرسوميات لتشغيل أثقل النماذج.' : 'Deploy heavy transformer topologies on VEXL H100 GPU clusters.'
      },
      {
        icon: Award,
        title: isAr ? 'الحرية الأكاديمية ونشر الأبحاث' : 'Publication Freedom',
        desc: isAr ? 'نشجع على نشر أبحاثك في الدوريات والمؤتمرات العالمية المرموقة.' : 'We actively support and finance paper publishing in IEEE, NeurIPS, and CVPR.'
      },
      {
        icon: MapPin,
        title: isAr ? 'الموقع الجغرافي والإرشاد' : 'Direct Mentorship',
        desc: isAr ? 'العمل مباشرة وجنباً إلى جنب مع فريق كبار مهندسي الذكاء المكاني والـ GIS.' : 'Pair-program with Staff GeoAI Architects on production spatial pipelines.'
      }
    ],
    formTitle: isAr ? 'طلب التقديم المبدئي' : 'Residency Application',
    formSubtitle: isAr ? 'يرجى تقديم بياناتك وموجز مقترحك البحثي.' : 'Submit your academic focus and research proposal outline.',
    fieldName: isAr ? 'الاسم بالكامل' : 'Full Name',
    fieldEmail: isAr ? 'البريد الإلكتروني' : 'Email Address',
    fieldDomain: isAr ? 'التخصص البحثي' : 'Research Domain',
    domains: {
      'spatial-ml': isAr ? 'التعلم الآلي المكاني (Spatial ML)' : 'Spatial Machine Learning',
      'climate': isAr ? 'النمذجة المناخية والبيئية' : 'Climate & Water Stress Forecasting',
      'autonomous-agents': isAr ? 'أنظمة الوكلاء المكانية المستقلة' : 'Autonomous Spatial Multi-Agent Systems'
    },
    fieldProposal: isAr ? 'ملخص المقترح البحثي' : 'Research Proposal Summary',
    btnSubmit: isAr ? 'إرسال المقترح' : 'Submit Application',
    successMsg: isAr ? 'تم استلام طلبك بنجاح! سيقوم مهندسو الأبحاث بمراجعته والرد عليك قريباً.' : 'Application received. Our research team will review your proposal and respond shortly.'
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.email && formData.proposal) {
      setFormSubmitted(true);
    } else {
      alert(isAr ? 'يرجى ملء جميع الحقول المطلوبة.' : 'Please fill all required fields.');
    }
  };

  return (
    <div className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-24 pb-24 ${isAr ? 'font-arabic' : 'font-sans'} overflow-hidden relative`}>
      <SEO 
        title={isAr ? 'برنامج الإقامة البحثية | فكسل' : 'Research Residency | VEXL'}
        description={isAr ? 'برنامج إقامة وبوابة تقديم الباحثين والأكاديميين لشركة فكسل.' : 'VEXL Research Residency program application portal for PhD candidates and GIS researchers.'}
        lang={lang}
      />

      <RasterGridBackdrop type="type-b" className="absolute inset-0 z-0 opacity-70 pointer-events-none" />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        
        {/* HERO SECTION */}
        <section className="text-center mt-12 mb-20 space-y-6">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight [letter-spacing:-0.02em] font-display text-[#EDF0F1]">
            {t.title}
          </h1>
          <p className="text-sm md:text-base text-[#9AA5AC] font-light max-w-xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </section>

        {/* TIMELINE IMAGE PLACEHOLDER */}
        <section className="mb-24">
          <ResearchImagePlaceholder
            labelEn="Research Fellowship residency stages and timeline blueprint"
            labelAr="مخطط مراحل ومسار برنامج الزمالة والإقامة البحثية لـ فكسل"
            filename="residency_timeline.png"
            dimensions="1200x500px (24:10)"
            isAr={isAr}
          />
        </section>

        {/* BENEFITS SECTION */}
        <section className="mb-24">
          <h2 className="text-xl md:text-2xl font-bold text-center mb-12 font-display text-[#EDF0F1]">{t.benefitsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.benefits.map((benefit, idx) => (
              <div key={idx} className="p-6 rounded-md border border-[#262C31] bg-[#12161A] space-y-4">
                <div className="w-10 h-10 rounded bg-[#171C21] border border-[#262C31] flex items-center justify-center text-[#4E9BB8]">
                  <benefit.icon size={18} />
                </div>
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-[#EDF0F1]">{benefit.title}</h4>
                  <p className="text-xs text-[#9AA5AC] font-light leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* APPLICATION FORM */}
        <section className="max-w-2xl mx-auto">
          <div className="p-8 rounded-lg border border-[#262C31] bg-[#12161A]/95 backdrop-blur-md relative overflow-hidden">
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#0E93A3]/40 to-transparent" />
            
            {!formSubmitted ? (
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="text-center space-y-2 mb-6">
                  <h3 className="text-lg font-bold font-display text-[#EDF0F1]">{t.formTitle}</h3>
                  <p className="text-xs text-[#9AA5AC] font-light">{t.formSubtitle}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Name field */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono text-[#9AA5AC] tracking-wider uppercase">{t.fieldName}</label>
                    <input 
                      type="text" 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-[#05070A] border border-[#262C31] rounded-sm px-4 py-3 text-xs text-[#EDF0F1] placeholder-[#626F79] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-colors"
                    />
                  </div>

                  {/* Email field */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-mono text-[#9AA5AC] tracking-wider uppercase">{t.fieldEmail}</label>
                    <input 
                      type="email" 
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full bg-[#05070A] border border-[#262C31] rounded-sm px-4 py-3 text-xs text-[#EDF0F1] placeholder-[#626F79] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-colors"
                    />
                  </div>
                </div>

                {/* Domain selector */}
                <div className="space-y-2">
                  <label className="text-[10px] font-mono text-[#9AA5AC] tracking-wider uppercase">{t.fieldDomain}</label>
                  <select
                    value={formData.domain}
                    onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                    className="w-full bg-[#05070A] border border-[#262C31] rounded-sm px-4 py-3 text-xs text-[#EDF0F1] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-colors"
                  >
                    {Object.entries(t.domains).map(([key, val]) => (
                      <option key={key} value={key} className="bg-[#05070A] text-[#EDF0F1]">{val}</option>
                    ))}
                  </select>
                </div>

                {/* Proposal text area */}
                <div className="space-y-2">
                  <label className="text-[10px] font-mono text-[#9AA5AC] tracking-wider uppercase">{t.fieldProposal}</label>
                  <textarea 
                    required
                    rows={4}
                    value={formData.proposal}
                    onChange={(e) => setFormData(prev => ({ ...prev, proposal: e.target.value }))}
                    className="w-full bg-[#05070A] border border-[#262C31] rounded-sm px-4 py-3 text-xs text-[#EDF0F1] placeholder-[#626F79] focus:outline-none focus:border-[#0E93A3] focus:ring-1 focus:ring-[#0E93A3] transition-colors resize-none"
                  />
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-sm bg-[#0E93A3] hover:bg-[#075C68] transition-all text-xs font-semibold tracking-wider uppercase text-white shadow-sm cursor-pointer"
                >
                  <Send size={12} />
                  <span>{t.btnSubmit}</span>
                </button>
              </form>
            ) : (
              <div className="py-12 text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-[#0E93A3]/10 border border-[#0E93A3]/30 flex items-center justify-center text-[#0E93A3] mx-auto">
                  <Check size={20} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-bold text-[#EDF0F1]">{isAr ? 'تم الإرسال بنجاح!' : 'Application Sent!'}</h3>
                  <p className="text-xs text-[#9AA5AC] font-light leading-relaxed max-w-sm mx-auto">
                    {t.successMsg}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
};

export default ResearchResidency;
